import React, { useRef, useState } from 'react';
import { faCheckCircle, faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classnames from 'classnames';
import { useDispatch } from 'react-redux';

import { upsertCard } from 'common/features/store/duck/wallet/duck';
import { Card } from 'common/api/e-comm/models/Card';
import { Modal, ModalSize } from '../../../components/modal/Modal';
import { Formik, FormikHelpers } from 'formik';
import { FormikTextField } from '../../../components/fields/FormikTextField';
import { ActivityIndicator } from 'components/activity-indicator/ActivityIndicator';
import { createStyles } from 'utils/createStyle';
import { colors } from 'styles/settings';
import { editCardDetails } from 'common/api/wallet/cards';
import { delay } from 'common/utils';
import { useRootSelector } from 'common/features/featuresReducer';
import {
    walletCardPinValidator,
    walletCardNameValidator,
} from 'common/modules/form-validation/validators';

interface EditCardModalProps {
    isOpen?: boolean;
    closeModal: () => void;
    card: Card;
}

interface EditCardFormValues {
    number: string;
    pin?: string;
    name?: string;
}

const styles = createStyles({
    submitButton: [
        'rounded-full bg-brand text-white font-semibold text-lg py-2 mb-2 w-full',
        { ':disabled': { backgroundColor: colors.greyAccent, color: colors.grey4 } },
    ],
    textInput: [
        'bg-grey-accent',
        {
            border: 'none',
            minWidth: 275,
            '&::-webkit-calendar-picker-indicator': { display: 'none !important' },
            '&::-webkit-list-button': { display: 'none !important' },
        },
    ],
    cardNumberInput: [{ ':disabled': { color: colors.grey3 } }],
});

const RetryUpdateCard = ({
    attempts,
    isSubmitting,
}: {
    attempts: number;
    isSubmitting: boolean;
}) => {
    return (
        <div>
            {attempts > 0 && attempts < 3 && (
                <button
                    disabled={isSubmitting}
                    type="submit"
                    className={classnames(styles.submitButton, 'w-full flex justify-center')}
                >
                    {!isSubmitting ? (
                        <span>Retry</span>
                    ) : (
                        <span>
                            <ActivityIndicator width="30" />
                        </span>
                    )}
                </button>
            )}
        </div>
    );
};

const EditCardModal = ({ isOpen, closeModal, card }: EditCardModalProps) => {
    const dispatch = useDispatch<any>();
    const submitAttempts = useRef<number>(0);
    const [formError, setFormError] = useState('');
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const { userId } = useRootSelector((s) => s.store.account);

    const editCardFormInitialValues = {
        number: (card && card.cardNumber) || '',
        pin: (card && card.pin) || '',
        name: (card && card.name) || '',
    };

    const onSubmit = async (
        values: EditCardFormValues,
        actions: FormikHelpers<EditCardFormValues>
    ) => {
        if (!userId) {
            return;
        }

        actions.setSubmitting(true);

        const errMsg =
            submitAttempts.current > 1
                ? 'Unable to update card. Please try again later.'
                : 'Unable to update card.';

        try {
            const result = await editCardDetails(card.id, values.name || '', values.pin || '');

            // add a small delay for better loading responsiveness
            await delay(1000);
            submitAttempts.current += 1;

            if (result.error) {
                actions.setSubmitting(false);
                setFormError(errMsg);
                return;
            }

            const updatedCard: Card = {
                ...card,
                pin: values.pin || '',
                name: values.name || '',
            };

            dispatch(upsertCard({ userId, card: updatedCard }));
            actions.setSubmitting(false);
            setSubmitSuccess(true);
        } catch (err) {
            actions.setSubmitting(false);
            setFormError(errMsg);
        }
    };

    const onClose = () => {
        setFormError('');
        setSubmitSuccess(false);
        submitAttempts.current = 0;
        closeModal();
    };

    return (
        <Modal
            title="Edit Card Details"
            isOpen={isOpen}
            onClose={onClose}
            size={[ModalSize.CONTENT_WIDTH, ModalSize.CONTENT_WIDTH]}
        >
            <Formik<EditCardFormValues>
                initialValues={editCardFormInitialValues}
                onSubmit={onSubmit}
            >
                {(formikProps) => (
                    <form
                        className="flex flex-col w-auto md:w-96"
                        noValidate
                        autoComplete="off"
                        onSubmit={formikProps.handleSubmit}
                    >
                        {formError && (
                            <label className="flex w-72 sm:w-full flex-col h-60 font-semibold items-center justify-center text-2xl mb-4">
                                <FontAwesomeIcon
                                    icon={faExclamationTriangle}
                                    size="lg"
                                    className="text-error mr-4"
                                />
                                <span className="text-center text-brand mt-4">{formError}</span>
                            </label>
                        )}
                        {!submitSuccess && (
                            <div
                                className={classnames(
                                    submitAttempts.current > 0 ? 'hidden' : 'block'
                                )}
                            >
                                <FormikTextField<EditCardFormValues>
                                    className={classnames(styles.textInput, styles.cardNumberInput)}
                                    wrapperStyles="mb-7"
                                    placeholder="Enter gift card number"
                                    label="Card Number"
                                    name="number"
                                    disabled
                                />
                                <FormikTextField<EditCardFormValues>
                                    className={styles.textInput}
                                    wrapperStyles="mb-7"
                                    placeholder="Enter security pin if available"
                                    label="PIN"
                                    name="pin"
                                    validate={(value) => {
                                        if (value.length) {
                                            return walletCardPinValidator(value);
                                        }
                                    }}
                                />
                                <FormikTextField<EditCardFormValues>
                                    className={styles.textInput}
                                    wrapperStyles="mb-12"
                                    placeholder="Enter custom card name"
                                    label="Card Name (Optional)"
                                    name="name"
                                    onChange={() => {
                                        formikProps.setFieldTouched('name');
                                    }}
                                    validate={(value) => {
                                        if (value.length) {
                                            return walletCardNameValidator(value);
                                        }
                                    }}
                                />
                                <button
                                    type="submit"
                                    className={styles.submitButton}
                                    disabled={
                                        formikProps.isSubmitting ||
                                        !formikProps.isValid ||
                                        !formikProps.dirty ||
                                        submitAttempts.current > 2 ||
                                        submitSuccess
                                    }
                                >
                                    {formikProps.isSubmitting ? 'Updating Card...' : 'Save'}
                                </button>
                            </div>
                        )}
                        {submitSuccess ? (
                            <React.Fragment>
                                <div
                                    style={{ minWidth: 275 }}
                                    className="text-success flex flex-col items-center mb-10 py-16"
                                >
                                    <FontAwesomeIcon icon={faCheckCircle} size="3x" />
                                    <h5 className="font-semibold text-2xl text-center mt-8">
                                        Card Successfully Updated
                                    </h5>
                                </div>
                            </React.Fragment>
                        ) : (
                            <RetryUpdateCard
                                attempts={submitAttempts.current}
                                isSubmitting={formikProps.isSubmitting}
                            />
                        )}
                        <button
                            onClick={(evt: React.SyntheticEvent) => {
                                evt.preventDefault();
                                formikProps.resetForm();
                                onClose();
                            }}
                            className="text-brand font-semibold underline text-lg py-2"
                        >
                            {!submitSuccess && submitAttempts.current === 0 ? 'Cancel' : 'Close'}
                        </button>
                    </form>
                )}
            </Formik>
        </Modal>
    );
};

export default EditCardModal;
